import {
  ArrowLeft,
  Lock,
  AlertTriangle,
  Send,
  Tag,
  ExternalLink,
  Printer,
  Shield,
  Clock,
  CheckCircle2,
  Wrench,
  ClipboardList,
  X,
  Repeat,
  XCircle,
  Package,
  Smartphone,
  ChevronDown,
  MoreHorizontal,
  FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const statusConfig = {
  pendiente_recibir: { label: 'Pendiente Recibir', icon: Clock, color: 'bg-yellow-500', step: 0 },
  recibida: { label: 'Recibida', icon: CheckCircle2, color: 'bg-blue-500', step: 1 },
  cuarentena: { label: 'Cuarentena', icon: AlertTriangle, color: 'bg-amber-700', step: 1.2 },
  en_taller: { label: 'En Taller', icon: Wrench, color: 'bg-purple-500', step: 2 },
  re_presupuestar: { label: 'Re-presupuestar', icon: AlertTriangle, color: 'bg-orange-500', step: 2.5 },
  reparado: { label: 'Reparado', icon: CheckCircle2, color: 'bg-green-500', step: 3 },
  validacion: { label: 'Validación', icon: ClipboardList, color: 'bg-indigo-500', step: 4 },
  enviado: { label: 'Enviado', icon: Send, color: 'bg-emerald-500', step: 5 },
  garantia: { label: 'Garantía', icon: Shield, color: 'bg-red-500', step: 0 },
  cancelado: { label: 'Cancelado', icon: X, color: 'bg-gray-500', step: -1 },
  reemplazo: { label: 'Reemplazo', icon: Repeat, color: 'bg-cyan-500', step: 5 },
  irreparable: { label: 'Irreparable', icon: XCircle, color: 'bg-red-600', step: -1 },
};

export function OrdenDetalleHeader({
  orden,
  metricas,
  onBack,
  onPrint,
  onPrintNoPrices,
  onPrintBlank,
  onDownloadWithNsys,
  canPrintWithPrices,
  onShowEtiqueta,
  onObtenerLinkSeguimiento,
  onCrearGarantia,
  creandoGarantia,
  onEnviarNotificacion,
  enviandoNotificacion,
  onShowCambioEstado,
  onGenerarRecogida,
  onMarcarIrreparable,
  onSolicitarRepresupuestar,
}) {
  const currentStatus = statusConfig[orden.estado];
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const puedeGenerarRecogida = !orden.codigo_recogida_entrada;

  const estadosEnTrabajo = ['recibida', 'en_taller', 'reparado', 'validacion'];
  const puedeRepresupuestar = estadosEnTrabajo.includes(orden.estado) && orden.estado !== 're_presupuestar';
  const puedeMarcarIrreparable = estadosEnTrabajo.includes(orden.estado) && orden.estado !== 'irreparable';
  const puedeCrearGarantia = orden.estado === 'enviado' && !orden.es_garantia;
  const tieneAccionesEspeciales = puedeRepresupuestar || puedeMarcarIrreparable || puedeCrearGarantia;

  const scrollToInfoTecnica = () => {
    const el = document.getElementById('info-tecnica');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('ring-2', 'ring-primary/60', 'ring-offset-2', 'ring-offset-background');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-primary/60', 'ring-offset-2', 'ring-offset-background');
      }, 1500);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Fila 1: título + badges */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <Button variant="ghost" size="icon" onClick={onBack} className="shrink-0 -ml-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {orden.numero_autorizacion ? (
                <h1 className="text-2xl font-bold tracking-tight font-mono text-primary" data-testid="auth-code-header">
                  {orden.numero_autorizacion}
                </h1>
              ) : (
                <h1 className="text-2xl font-bold tracking-tight font-mono">{orden.numero_orden}</h1>
              )}
              <Badge className={`badge-status status-${orden.estado}`}>
                {orden.es_garantia && orden.garantia_resultado === 'no_procede'
                  ? 'Fuera de Garantía'
                  : currentStatus?.label}
              </Badge>
              {orden.es_garantia && (
                <Badge
                  className="bg-orange-500/15 text-orange-400 border border-orange-500/30 gap-1 hover:bg-orange-500/20"
                  title={orden.numero_orden_padre ? `Garantía de ${orden.numero_orden_padre}` : 'Orden de garantía'}
                  data-testid="badge-garantia-header"
                >
                  <Shield className="w-3 h-3" />
                  GARANTÍA
                </Badge>
              )}
              {orden.es_garantia && orden.garantia_resultado === 'no_procede' && (
                <Badge variant="outline" className="gap-1 border-orange-500/50 text-orange-400 bg-orange-500/10">
                  <AlertTriangle className="w-3 h-3" />
                  No cubierto
                </Badge>
              )}
              {orden.bloqueada && (
                <Badge variant="destructive" className="gap-1">
                  <Lock className="w-3 h-3" />
                  BLOQUEADA
                </Badge>
              )}
              {metricas?.en_retraso && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  RETRASO: {metricas.retraso_dias} días
                </Badge>
              )}
              {orden.consentimiento_legal && (
                <Badge
                  className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 gap-1 hover:bg-emerald-500/20"
                  title={
                    orden.firma_digital?.fecha
                      ? `Firmado vía web el ${new Date(orden.firma_digital.fecha).toLocaleString('es-ES')}`
                      : 'Consentimiento firmado vía web'
                  }
                  data-testid="badge-consentimiento-header"
                >
                  <Shield className="w-3 h-3" />
                  CONSENTIMIENTO FIRMADO
                </Badge>
              )}
            </div>
            <div className="text-muted-foreground mt-1.5 text-xs flex flex-wrap items-center gap-x-3 gap-y-1">
              {orden.es_garantia && (
                <span className="inline-flex items-center gap-1 font-semibold text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-0.5">
                  <Shield className="w-3 h-3" />
                  GARANTÍA de {orden.numero_orden_padre || 'orden padre'}
                  {orden.orden_padre_id && (
                    <a href={`/ordenes/${orden.orden_padre_id}`} className="underline ml-1 hover:text-rose-300">Ver original</a>
                  )}
                </span>
              )}
              {orden.numero_autorizacion && (
                <span className="font-mono">OT: {orden.numero_orden}</span>
              )}
              <span>
                Creada el {new Date(orden.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
              </span>
            </div>
          </div>
        </div>

        {/* Acciones primary alineadas al título (siempre visibles) */}
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={onEnviarNotificacion}
            disabled={enviandoNotificacion}
            className="gap-1.5"
            data-testid="notificar-btn"
          >
            <Send className="w-4 h-4" />
            <span className="hidden sm:inline">{enviandoNotificacion ? 'Enviando…' : 'Notificar'}</span>
          </Button>
          <Button size="sm" onClick={onShowCambioEstado} data-testid="change-status-btn" className="gap-1.5">
            <CheckCircle2 className="w-4 h-4" />
            Cambiar estado
          </Button>
        </div>
      </div>

      {/* Fila 2: toolbar de acciones secundarias */}
      <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 rounded-xl bg-muted/40 border border-border">
        {/* Atajo info técnica */}
        <Button
          variant="ghost"
          size="sm"
          onClick={scrollToInfoTecnica}
          data-testid="btn-info-tecnica"
          className="h-8 px-2.5 gap-1.5 text-foreground hover:bg-accent"
          title="Ir a Información del dispositivo"
        >
          <Smartphone className="w-4 h-4" />
          <span>Info técnica</span>
        </Button>

        <div className="w-px h-5 bg-border mx-1" />

        {/* Logística */}
        {puedeGenerarRecogida && onGenerarRecogida && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={onGenerarRecogida}
              className="h-8 px-2.5 gap-1.5 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
              data-testid="btn-generar-recogida-header"
            >
              <Package className="w-4 h-4" />
              Recogida
            </Button>
            <div className="w-px h-5 bg-border mx-1" />
          </>
        )}

        {/* Imprimir dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 px-2.5 gap-1.5" data-testid="print-menu-trigger">
              <Printer className="w-4 h-4" />
              Imprimir
              <ChevronDown className="w-3 h-3 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel className="text-xs">Documento PDF</DropdownMenuLabel>
            {canPrintWithPrices && (
              <DropdownMenuItem onClick={onPrint} data-testid="print-pdf-btn">
                <Printer className="w-4 h-4 mr-2" />
                PDF (Completo con precios)
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onPrintNoPrices} data-testid="print-pdf-no-prices-btn">
              <Printer className="w-4 h-4 mr-2" />
              PDF (Sin precios)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onPrintBlank} data-testid="print-blank-template-btn">
              <Printer className="w-4 h-4 mr-2" />
              Plantilla en blanco
            </DropdownMenuItem>
            {canPrintWithPrices && onDownloadWithNsys && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onDownloadWithNsys} data-testid="download-pdf-with-nsys-btn">
                  <FileText className="w-4 h-4 mr-2 text-blue-600" />
                  <div className="flex flex-col items-start">
                    <span>Descargar PDF Validación</span>
                    <span className="text-[10px] text-muted-foreground">Archivo .pdf con todos los datos</span>
                  </div>
                </DropdownMenuItem>
              </>
            )}
            {isAdmin() && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate(`/crm/imprimir-pericial?orden_id=${orden.id}`)}
                  data-testid="print-pericial-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Informe técnico…
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Etiqueta + Link */}
        <Button variant="ghost" size="sm" onClick={onShowEtiqueta} className="h-8 px-2.5 gap-1.5">
          <Tag className="w-4 h-4" />
          Etiqueta
        </Button>
        <Button variant="ghost" size="sm" onClick={onObtenerLinkSeguimiento} data-testid="link-seguimiento-btn" className="h-8 px-2.5 gap-1.5">
          <ExternalLink className="w-4 h-4" />
          Link
        </Button>

        {/* Acciones especiales agrupadas en "Más" si hay más de una */}
        {tieneAccionesEspeciales && (
          <>
            <div className="w-px h-5 bg-border mx-1" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 px-2.5 gap-1.5" data-testid="menu-acciones-especiales">
                  <MoreHorizontal className="w-4 h-4" />
                  Acciones
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel className="text-xs">Acciones especiales</DropdownMenuLabel>
                {puedeCrearGarantia && (
                  <DropdownMenuItem
                    onClick={onCrearGarantia}
                    disabled={creandoGarantia}
                    className="text-orange-400 focus:text-orange-300 focus:bg-orange-500/10"
                    data-testid="crear-garantia-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    {creandoGarantia ? 'Creando…' : 'Crear garantía'}
                  </DropdownMenuItem>
                )}
                {puedeRepresupuestar && onSolicitarRepresupuestar && (
                  <DropdownMenuItem
                    onClick={onSolicitarRepresupuestar}
                    className="text-orange-400 focus:text-orange-300 focus:bg-orange-500/10"
                    data-testid="btn-re-presupuestar"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Re-presupuestar
                  </DropdownMenuItem>
                )}
                {puedeMarcarIrreparable && onMarcarIrreparable && (
                  <>
                    {(puedeCrearGarantia || puedeRepresupuestar) && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      onClick={onMarcarIrreparable}
                      className="text-red-400 focus:text-red-300 focus:bg-red-500/10"
                      data-testid="btn-irreparable"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Marcar irreparable
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
    </div>
  );
}

export { statusConfig };
